<template>
  <ui-modal-layout
    :title="tr('Delete automation')"
    :description="tr('Warning: Deleting this automation will permanently remove it from your account. This action cannot be undone. If the automation is linked to a campaign, you have the option to also delete the associated campaign. Please confirm your choice below.')"
    :icon="Trash2Icon"
    tone="destructive"
  >
    <div v-if="showCampaignDeletionOptions" class="flex flex-col gap-3">
      <label v-if="!showDeletedCampaignMessage" class="flex cursor-pointer items-center gap-2.5 text-sm text-foreground">
        <ui-checkbox v-model="deleteCampaign" />
        {{ tr('Also delete associated campaign') }}
      </label>
      <info-text v-else>
        {{ tr('The associated campaign has already been deleted.') }}
      </info-text>

      <info-text v-if="deleteCampaign" type="warning">
        {{ tr('Warning: This action will permanently delete the campaign and all associated data. This action cannot be undone.') }}
      </info-text>
    </div>

    <template #footer>
      <ui-button variant="outline" :disabled="isLoading" @click="() => close()">
        {{ tr('Cancel') }}
      </ui-button>
      <ui-button variant="destructive" :disabled="isLoading" @click="() => actionConfirm()">
        <loader-circle-icon v-if="isLoading" class="animate-spin" />
        {{ tr('Confirm') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { LoaderCircle, Trash2 } from '@lucide/vue';
import { markRaw } from 'vue';

import { CAMPAIGN_STATUS } from '@/consts';
import Rule from '@/entities/Rule.js';
import RuleService from '@/services/Rule.js';
import InfoText from '@/ui/components/InfoText.vue';

export default {
  name: 'DeleteRuleConfirmModalContent',

  components: {
    InfoText,
    LoaderCircleIcon: LoaderCircle,
  },

  props: {
    rule: {
      type: Rule,
      required: true,
    },
  },

  data: () => ({
    Trash2Icon: markRaw(Trash2),
    isLoading: false,
    deleteCampaign: false,
  }),

  computed: {
    showCampaignDeletionOptions() {
      return this.rule.has_output_campaign;
    },
    showDeletedCampaignMessage() {
      return (
        this.rule.has_output_campaign &&
        this.rule.campaign_status === CAMPAIGN_STATUS.DELETED
      );
    },
  },

  methods: {
    close() {
      this.$emit('close');
    },
    actionConfirm() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      const { id } = this.rule;
      RuleService.delete(id, this.deleteCampaign)
        .then(resp => {
          this.$store.commit('rules/ruleRemoved', id);
          this.$toast.clear();
          this.$toast.success(this.tr('The automation was successfully deleted.'));
          this.$router.replace('/automation');

          this.close();

          return resp;
        })
        .catch(() => {
          this.isLoading = false;
          this.$toast.error(this.tr('An error occurred while deleting the automation.'));
        })
        .finally(() => {
          this.progress = false;
          this.$emit('close');
        });
    },
  },
};
</script>
